import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-router';
import { getUser } from '@frontend/auth-helpers';
import { WeaveLogo } from '@frontend/integrations';
import { theme } from '@frontend/theme';
import { Avatar, Button, PopoverMenu, PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';

interface NavbarProps {
  refreshText?: string;
  isUserAuthenticated: boolean;
  isUserRefreshing?: boolean;
  onRefreshClick?: () => void;
  onLogoutClick?: () => void;
}

const Navbar = ({
  refreshText,
  isUserAuthenticated,
  isUserRefreshing,
  onRefreshClick = () => {},
  onLogoutClick = () => {},
}: NavbarProps) => {
  const navigate = useNavigate();
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({
    placement: 'bottom-end',
    interactionOptions: {
      hover: { enabled: true },
      click: { enabled: false },
    },
  });

  const userName = [getUser()?.firstName ?? '', getUser()?.lastName ?? ''].filter(Boolean).join(' ') || 'Guest User';

  const handleLogoutClick = () => {
    close();
    onLogoutClick();
  };

  return (
    <nav css={navbarStyle}>
      <WeaveLogo height={30} width={130} css={imageHoverStyle} onClick={() => navigate({ to: '/' })} />
      {isUserAuthenticated ? (
        <div css={buttonContainerStyle}>
          <Text color='white' weight='bold'>
            {refreshText}
          </Text>
          <Button
            onClick={onRefreshClick}
            loading={isUserRefreshing}
            variant='secondary'
            size='large'
            css={refreshButtonStyle}
          >
            Refresh
          </Button>
          <Avatar
            name={userName}
            size='large'
            disableHoverLabel
            disableClick
            {...getTriggerProps()}
            css={imageHoverStyle}
          />
          <PopoverMenu {...getMenuProps()}>
            <PopoverMenuItem {...getItemProps({ index: 0 })} onClick={handleLogoutClick} css={popoverMenuStyle}>
              Logout
            </PopoverMenuItem>
          </PopoverMenu>
        </div>
      ) : null}
    </nav>
  );
};

export default Navbar;

const navbarStyle = css`
  background-color: ${theme.colors.text.default};
  height: ${theme.spacing(12)};
  width: 100%;
  padding: ${theme.spacing(4)};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const imageHoverStyle = css`
  cursor: pointer;
  &:hover {
    opacity: 0.8;
    transition: all 0.5s ease-in-out;
  }
`;

const buttonContainerStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
`;

const refreshButtonStyle = css`
  background-color: transparent;
  color: ${theme.colors.text.white};
  border: 1px solid ${theme.colors.text.white};
  outline: none;
  font-weight: 500;
  width: 100px;
  &:hover {
    background-color: transparent;
    transform: scale(1.02);
    transition: all 0.5s ease-in-out;
  }
`;

const popoverMenuStyle = css`
  min-width: 150px;
`;
